import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

const { CancelToken } = window.axios;

const store = new Vuex.Store({
  state: {
    debug: false,
    boxSpecifications: null,
    activeBoxSpecificationId: null,
    modals: null,
    serverErrors: null,
    formInput: {},
    responseCustomOffers: null,
    responseStockOffers: null,
    responseOptions: {},
    fetchPricesCancelToken: null,
    fetchPricesIsLoading: false,
    activeCustomOffer: null,
    modalOpen: false,
    modalTitle: null,
    modalContent: null,
    i18n: {},
  },

  getters: {
    boxSpecifications: (state) => state.boxSpecifications,
    formInput: (state) => state.formInput,
    modals: (state) => state.modals,
    serverErrors: (state) => state.serverErrors,
    responseCustomOffers: (state) => state.responseCustomOffers,
    responseStockOffers: (state) => state.responseStockOffers,
    responseOptions: (state) => state.responseOptions,
    modalOpen: (state) => state.modalOpen,
    modalTitle: (state) => state.modalTitle,
    modalContent: (state) => state.modalContent,
    activeBoxSpecificationId: (state) => state.activeBoxSpecificationId,
    fetchPricesIsLoading: (state) => state.fetchPricesIsLoading,
    activeCustomOffer: (state) => state.activeCustomOffer,

    modalForIdentifier: (state) => (identifier) => {
      if (typeof state.modals[identifier] === 'undefined') {
        return null;
      }

      return state.modals[identifier];
    },

    flatServerErrors: (state) => {
      if (!state.serverErrors) {
        return null;
      }

      return Object.keys(state.serverErrors).map((key) => state.serverErrors[key]).flat(1);
    },

    activeBoxSpecification: (state) => {
      if (!state.activeBoxSpecificationId) {
        return null;
      }

      return state.boxSpecifications[state.activeBoxSpecificationId];
    },
  },

  mutations: {
    setBoxSpecifications(state, boxSpecifications) {
      state.boxSpecifications = boxSpecifications;
    },

    setActiveBoxSpecificationId(state, id) {
      state.activeBoxSpecificationId = id;
    },

    setModals(state, modals) {
      state.modals = modals;
    },

    setFormInput(state, [key, value]) {
      state.formInput = { ...state.formInput, [key]: value };

      if (state.debug) {
        // eslint-disable-next-line no-console
        console.log('state.formInput', state.formInput);
      }
    },

    setActiveCustomOffer(state, offer) {
      state.activeCustomOffer = offer;
    },

    setServerErrors(state, serverErrors) {
      state.serverErrors = serverErrors;

      if (state.debug) {
        // eslint-disable-next-line no-console
        console.log('state.serverErrors', state.serverErrors);
      }
    },

    setDebug(state, debugState) {
      state.debug = debugState;
    },

    setResponseCustomOffers(state, customOffers) {
      if (typeof customOffers === 'undefined' || !Array.isArray(customOffers) || customOffers.length === 0) {
        state.responseCustomOffers = null;
        return;
      }

      state.responseCustomOffers = customOffers;

      if (state.responseCustomOffers.length) {
        [state.activeCustomOffer] = state.responseCustomOffers;
      }
    },

    setResponseStockOffers(state, stockOffers) {
      if (typeof stockOffers === 'undefined' || !Array.isArray(stockOffers) || stockOffers.length === 0) {
        state.responseStockOffers = null;
        return;
      }

      state.responseStockOffers = stockOffers;
    },

    setResponseOptions(state, options) {
      const newOptions = {};

      newOptions.title = typeof options.title !== 'undefined' ? options.title : null;
      newOptions.list = typeof options.list !== 'undefined' ? options.list : null;
      newOptions.dimensions = typeof options.dimensions !== 'undefined' ? options.dimensions : null;
      newOptions.dimensionsLabel = typeof options.dimensionsLabel !== 'undefined' ? options.dimensionsLabel : null;
      newOptions.usps = typeof options.usps !== 'undefined' ? options.usps : null;
      newOptions.stockUsps = typeof options.stockUsps !== 'undefined' ? options.stockUsps : null;
      newOptions.offerSuggestion = typeof options.offerSuggestion !== 'undefined' ? options.offerSuggestion : null;
      newOptions.offerSuggestionUrl = typeof options.offerSuggestionUrl !== 'undefined' ? options.offerSuggestionUrl : null;

      state.responseOptions = newOptions;
    },

    setModalOpen(state, openState) {
      state.modalOpen = openState;
    },

    setModalTitle(state, title) {
      state.modalTitle = title;
    },

    setModalContent(state, content) {
      state.modalContent = content;
    },

    setFetchPricesCancelToken(state, cancelToken) {
      state.fetchPricesCancelToken = cancelToken;
    },

    setFetchPricesIsLoading(state, loadingState) {
      state.fetchPricesIsLoading = loadingState;
    },

    setI18n(state, i18n) {
      state.i18n = typeof i18n === 'string' ? JSON.parse(i18n) : i18n;
    },

    cleanPriceResponse(state) {
      state.serverErrors = null;
      state.responseOptions = {};
      state.responseCustomOffers = null;
      state.responseStockOffers = null;
    },
  },

  actions: {
    fetchPrices({ commit, dispatch, state }, formInput) {
      commit('setServerErrors', null);
      commit('setFetchPricesIsLoading', true);
      commit('cleanPriceResponse');

      formInput.fefco = state.activeBoxSpecificationId;

      // We use cancel token to stop multiple requests from overlapping.
      // When a request is started below we request a cancel token, which
      // is essentially a promise we can use to cancel the request.
      // When an active cancel token is found, we call it to stop the request.
      if (state.fetchPricesCancelToken) {
        state.fetchPricesCancelToken();

        commit('setFetchPricesCancelToken', null);
      }

      return window.axios.post('/api/box-guide', formInput, {
        cancelToken: new CancelToken((token) => {
          commit('setFetchPricesCancelToken', token);
        }),
      })
        .then((res) => {
          commit('setFetchPricesCancelToken', null);
          commit('setFetchPricesIsLoading', false);
          dispatch('handleFetchPricesResponse', res.data);
        })
        .catch((err) => {
          if (typeof err.toString === 'function' && err.toString() === 'Cancel') {
            return;
          }

          commit('setFetchPricesIsLoading', false);

          if (typeof err.response !== 'undefined' && typeof err.response.data !== 'undefined') {
            if (typeof err.response.data.errors !== 'undefined') {
              commit('setServerErrors', err.response.data.errors);
              return;
            }

            if (typeof err.response.data.message !== 'undefined') {
              commit('setServerErrors', [err.response.data.message]);
              return;
            }
          }

          commit('setServerErrors', [state.i18n.general_api_error]);
        });
    },

    addCustomOfferToCart({ commit }, formInput) {
      commit('setServerErrors', null);

      return window.axios.post('/api/box-guide-cart', formInput)
        .then((res) => {
          window.location = res.data.redirect;
        })
        .catch((err) => {
          if (typeof err.response !== 'undefined' && typeof err.response.data !== 'undefined') {
            commit('setServerErrors', err.response.data.errors);
          }
        });
    },

    setActiveBoxSpecification({
      commit,
      state,
      getters,
      dispatch,
    }, id) {
      commit('setActiveBoxSpecificationId', id);
      dispatch('cleanupFormInput');

      const specification = state.boxSpecifications[id];

      if (typeof specification !== 'undefined' && typeof specification.defaults !== 'undefined') {
        const { defaults } = getters.activeBoxSpecification;

        Object.keys(defaults).forEach((defaultKey) => {
          commit('setFormInput', [defaultKey, defaults[defaultKey]]);
        });
      }

      if (state.debug) {
        // eslint-disable-next-line no-console
        console.log('state.activeBoxSpecificationId', state.activeBoxSpecificationId);
      }
    },

    handleFetchPricesResponse({ commit, state }, res) {
      if (state.debug) {
        // eslint-disable-next-line no-console
        console.log('/api/box-guide response', res);
      }

      if (typeof res.errors !== 'undefined') {
        commit('setServerErrors', res.errors);
        return;
      }

      commit('setResponseOptions', {
        title: res.title,
        list: res.list,
        dimensions: res.dimensions,
        dimensionsLabel: res.dimensions_label,
        usps: res.usps,
        stockUsps: res.stock_usps,
        offerSuggestion: res.offer_suggestion,
        offerSuggestionUrl: res.offer_suggestion_url,
      });
      commit('setResponseCustomOffers', res.custom_offers);
      commit('setResponseStockOffers', res.stock_offers);
    },

    /**
     * After switching the box specification, some fields may become
     * (un)available. Because these fields may be set when another
     * box was selected, the fields might be send to the API.
     *
     * This methods checks if the current box specification has certain fields,
     * and if not the formInput is removed for those fields.
     *
     * @param getters
     * @param commit
     */
    cleanupFormInput({ getters, commit }) {
      if (!getters.activeBoxSpecification || !getters.activeBoxSpecification.dimensions) {
        return;
      }

      const {
        dimensions,
        handles,
        print,
        color,
      } = getters.activeBoxSpecification;

      const dimensionsToCleanup = ['length', 'width', 'height'];

      dimensionsToCleanup.forEach((dimension) => {
        if (!dimensions[dimension]) {
          commit('setFormInput', [dimension, null]);
        }
      });

      if (!handles) {
        commit('setFormInput', ['handles', null]);
      }

      if (!print) {
        commit('setFormInput', ['print', null]);
      }

      if (!color) {
        commit('setFormInput', ['color', null]);
      }
    },
  },
});

export default store;
